import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  RefreshCw, 
  Clock, 
  CheckCircle, 
  XCircle, 
  User, 
  Mail, 
  Calendar,
  DollarSign,
  AlertCircle,
  Loader2
} from 'lucide-react';

import { API } from '@/config';

const RefundRequestsManagement = () => {
  const { i18n } = useTranslation();
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
  const [processing, setProcessing] = useState(false);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/refund-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching refund requests:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في تحميل الطلبات' : 'Error loading requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [token]);

  const handleAction = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setActionModalOpen(true);
  };

  const processAction = async () => {
    if (!selectedRequest || !actionType) return;

    setProcessing(true);
    try {
      await axios.put(
        `${API}/admin/refund-requests/${selectedRequest.id}?status=${actionType === 'approve' ? 'approved' : 'rejected'}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(
        actionType === 'approve'
          ? (i18n.language === 'ar' ? 'تمت الموافقة وإرجاع المبلغ بنجاح! ✅' : i18n.language === 'sv' ? 'Godkänd och återbetald! ✅' : 'Approved and refunded! ✅')
          : (i18n.language === 'ar' ? 'تم رفض الطلب' : i18n.language === 'sv' ? 'Begäran avvisad' : 'Request rejected')
      );

      setActionModalOpen(false);
      setSelectedRequest(null);
      setActionType(null);
      fetchRequests();
    } catch (error) {
      console.error('Error processing request:', error);
      toast.error(error.response?.data?.detail || 'Error processing request');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 me-1" /> {i18n.language === 'ar' ? 'قيد الانتظار' : i18n.language === 'sv' ? 'Väntande' : 'Pending'}</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 me-1" /> {i18n.language === 'ar' ? 'تمت الموافقة' : i18n.language === 'sv' ? 'Godkänd' : 'Approved'}</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 me-1" /> {i18n.language === 'ar' ? 'مرفوض' : i18n.language === 'sv' ? 'Avvisad' : 'Rejected'}</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length
  };

  return (
    <div className="space-y-6" data-testid="refund-requests-management">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <RefreshCw className="w-7 h-7 text-amber-600" />
            {i18n.language === 'ar' ? 'طلبات الاسترداد' : i18n.language === 'sv' ? 'Återbetalningsbegäranden' : 'Refund Requests'}
          </h2>
          <p className="text-slate-600 mt-1">
            {i18n.language === 'ar' ? 'إدارة طلبات استرداد الأموال من العملاء' : i18n.language === 'sv' ? 'Hantera kunders återbetalningsbegäranden' : 'Manage customer refund requests'}
          </p>
        </div>
        <Button onClick={fetchRequests} variant="outline" disabled={loading}>
          <RefreshCw className={`w-4 h-4 me-2 ${loading ? 'animate-spin' : ''}`} />
          {i18n.language === 'ar' ? 'تحديث' : i18n.language === 'sv' ? 'Uppdatera' : 'Refresh'}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100">
          <div className="text-sm text-blue-600 font-medium">{i18n.language === 'ar' ? 'الإجمالي' : 'Total'}</div>
          <div className="text-3xl font-bold text-blue-900">{stats.total}</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100">
          <div className="text-sm text-amber-600 font-medium">{i18n.language === 'ar' ? 'قيد الانتظار' : 'Pending'}</div>
          <div className="text-3xl font-bold text-amber-900">{stats.pending}</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100">
          <div className="text-sm text-green-600 font-medium">{i18n.language === 'ar' ? 'تمت الموافقة' : 'Approved'}</div>
          <div className="text-3xl font-bold text-green-900">{stats.approved}</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-red-50 to-red-100">
          <div className="text-sm text-red-600 font-medium">{i18n.language === 'ar' ? 'مرفوض' : 'Rejected'}</div>
          <div className="text-3xl font-bold text-red-900">{stats.rejected}</div>
        </Card>
      </div>

      {/* Pending Requests Alert */}
      {stats.pending > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
          <div>
            <p className="font-semibold text-amber-900">
              {i18n.language === 'ar' 
                ? `لديك ${stats.pending} طلب استرداد بانتظار المراجعة` 
                : i18n.language === 'sv'
                ? `Du har ${stats.pending} återbetalningsbegäranden som väntar på granskning`
                : `You have ${stats.pending} refund request(s) pending review`}
            </p>
          </div>
        </div>
      )}

      {/* Requests List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        </div>
      ) : requests.length === 0 ? (
        <Card className="p-12 text-center">
          <RefreshCw className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 text-lg">
            {i18n.language === 'ar' ? 'لا توجد طلبات استرداد' : i18n.language === 'sv' ? 'Inga återbetalningsbegäranden' : 'No refund requests'}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <Card key={request.id} className={`p-5 ${request.status === 'pending' ? 'border-amber-300 bg-amber-50/30' : ''}`}>
              <div className="flex flex-col md:flex-row justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{request.user_name}</p>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {request.user_email}
                      </p>
                    </div>
                    {getStatusBadge(request.status)}
                  </div>

                  <div className="bg-slate-50 rounded-lg p-3 mb-3">
                    <p className="text-sm text-slate-600 font-medium mb-1">
                      {i18n.language === 'ar' ? 'سبب الاسترداد:' : 'Reason:'}
                    </p>
                    <p className="text-slate-800">{request.reason}</p>
                  </div>

                  <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4" />
                      {request.subscription_plan}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(request.created_at).toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : i18n.language === 'sv' ? 'sv-SE' : 'en-US')}
                    </span>
                  </div>
                </div>

                {request.status === 'pending' && (
                  <div className="flex gap-2 md:flex-col">
                    <Button
                      onClick={() => handleAction(request, 'approve')}
                      className="bg-green-500 hover:bg-green-600 text-white flex-1"
                      data-testid={`approve-refund-${request.id}`}
                    >
                      <CheckCircle className="w-4 h-4 me-1" />
                      {i18n.language === 'ar' ? 'موافقة' : i18n.language === 'sv' ? 'Godkänn' : 'Approve'}
                    </Button>
                    <Button
                      onClick={() => handleAction(request, 'reject')}
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50 flex-1"
                      data-testid={`reject-refund-${request.id}`}
                    >
                      <XCircle className="w-4 h-4 me-1" />
                      {i18n.language === 'ar' ? 'رفض' : i18n.language === 'sv' ? 'Avvisa' : 'Reject'}
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Action Confirmation Modal */}
      <Dialog open={actionModalOpen} onOpenChange={setActionModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${actionType === 'approve' ? 'text-green-600' : 'text-red-600'}`}>
              {actionType === 'approve' ? <CheckCircle className="w-6 h-6" /> : <XCircle className="w-6 h-6" />}
              {actionType === 'approve' 
                ? (i18n.language === 'ar' ? 'تأكيد الموافقة والاسترداد' : 'Confirm Approval & Refund')
                : (i18n.language === 'ar' ? 'تأكيد الرفض' : 'Confirm Rejection')}
            </DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4 mt-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="font-semibold text-slate-900">{selectedRequest.user_name}</p>
                <p className="text-sm text-slate-500">{selectedRequest.user_email}</p>
              </div>

              {actionType === 'approve' ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-700">
                    {i18n.language === 'ar' 
                      ? 'سيتم إرجاع المبلغ تلقائياً للعميل عبر Stripe وإلغاء اشتراكه.'
                      : 'The amount will be automatically refunded to the customer via Stripe and their subscription will be cancelled.'}
                  </p>
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-700">
                    {i18n.language === 'ar' 
                      ? 'سيتم إعلام العميل برفض طلب الاسترداد.'
                      : 'The customer will be notified that their refund request has been rejected.'}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setActionModalOpen(false)} disabled={processing}>
              {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              onClick={processAction}
              disabled={processing}
              className={actionType === 'approve' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}
            >
              {processing ? (
                <Loader2 className="w-4 h-4 me-2 animate-spin" />
              ) : actionType === 'approve' ? (
                <CheckCircle className="w-4 h-4 me-2" />
              ) : (
                <XCircle className="w-4 h-4 me-2" />
              )}
              {i18n.language === 'ar' ? 'تأكيد' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RefundRequestsManagement;
